import React from 'react';
import { bool, number, shape, string } from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { AUTHORIZATION_TYPE, AUTHORIZATION_TYPES } from 'feature/loading-management/utils/const';
import * as R from 'ramda';

// utils
import { formatNumber } from 'utils/helpers/format';

// styles
import { limitTextStyles, unlimitedTextStyles } from '../SupplierAuthCard.styles';

const SupplierAuthLimit = ({ active, limitData, unlimited }) => {
  // limitData will also contain property "limitType", removed for lint
  const { limitMet, limitType, limitVolume, loadableVolume, loadableVolumeUnitOfMeasure } =
    limitData;
  const theme = useTheme();

  const boxProps = {
    alignItems: 'center',
    borderRadius: 0.5,
    display: 'flex',
    height: 64,
    justifyContent: 'center',
    mb: 1,
  };

  const gallonLimit = limitVolume ? formatNumber(limitVolume) : '--';
  const loadableVolumeAmount = loadableVolume
    ? `${formatNumber(loadableVolume)} ${R.toLower(loadableVolumeUnitOfMeasure)}`
    : '--';

  const renderBody = () => {
    return unlimited ? (
      <Box bgcolor="graphic.light" {...boxProps}>
        <Typography sx={unlimitedTextStyles(theme)}>Unlimited</Typography>
      </Box>
    ) : limitType === AUTHORIZATION_TYPE.SELF ? (
      <Box
        {...boxProps}
        sx={{ flexDirection: 'row', gridGap: '10px', justifyContent: 'space-around' }}
      >
        <Box
          {...boxProps}
          sx={{
            bgcolor: 'success.light',
            flexDirection: 'column',
            justifyItems: 'center',
            p: 1,
            width: '50%',
          }}
        >
          <Typography>{gallonLimit}</Typography>
          <Typography variant="body2">Gallon Limit</Typography>
        </Box>
        <Box
          {...boxProps}
          sx={{
            bgcolor: 'success.light',
            flexDirection: 'column',
            justifyItems: 'center',
            p: 1,
            width: '50%',
          }}
        >
          <Typography>{loadableVolumeAmount}</Typography>
          <Typography variant="body2">Loadable Volume</Typography>
        </Box>
      </Box>
    ) : (
      <Box
        bgcolor={limitMet ? 'error.light' : 'success.light'}
        pb={1}
        pt="6px"
        {...boxProps}
        flexDirection="column"
      >
        <Typography sx={limitTextStyles({ limitMet, theme })} variant="subtitle1">
          {formatNumber(limitVolume)}
        </Typography>
        <Typography color="textPrimary" variant="body2">
          Gallon {AUTHORIZATION_TYPES[limitType]} Limit {limitMet ? 'Met' : ''}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {active ? (
        renderBody()
      ) : (
        <Box border={1} borderColor={theme.palette.graphic.light} {...boxProps}>
          <Typography variant="subtitle1">Inactive</Typography>
        </Box>
      )}
    </>
  );
};

export default SupplierAuthLimit;

SupplierAuthLimit.propTypes = {
  active: bool.isRequired,
  limitData: shape({
    limitMet: bool,
    limitType: string,
    limitVolume: number,
  }),
  unlimited: bool,
};

SupplierAuthLimit.defaultProps = {
  limitData: {
    limitMet: null,
    limitType: '',
    limitVolume: null,
  },
  unlimited: null,
};
